import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import * as dateFns from 'date-fns';
import {
  CustomValidatorFn,
  ICustomError,
  ValidationShowType,
  IValidatorConfig,
} from '~app/shared/interfaces/custom-validator';

interface IRequiredConfig extends IValidatorConfig {
  falseIsValid?: boolean;
}

interface IMinLengthConfig extends IValidatorConfig {
  min: number;
}

interface IMaxLengthConfig extends IValidatorConfig {
  max: number;
}

interface IMaxValueConfig extends IValidatorConfig {
  maxValue: number;
}

interface IMinValueConfig extends IValidatorConfig {
  minValue: number;
}

interface IDateRangeConfig extends IValidatorConfig {
  endDate: UntypedFormControl | AbstractControl;
}

interface IFileExtensionConfig extends IValidatorConfig {
  extensions: Array<string>;
}

interface IFileSizeConfig extends IValidatorConfig {
  maxSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class ValidatorsService {
  public required(config?: IRequiredConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'Required information') as IRequiredConfig;
    if (config.falseIsValid === undefined) config.falseIsValid = true;
    return control => {
      if (
        control.value === undefined ||
        control.value === null ||
        control.value === '' ||
        (!config.falseIsValid && control.value === false)
      ) {
        return {
          required: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
            falseIsValid: config.falseIsValid,
          },
        };
      }
      return undefined;
    };
  }

  public minLength(config: IMinLengthConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'Has min length') as IMinLengthConfig;
    return control => {
      if (control.value && control.value.length && control.value.length < config.min) {
        return {
          minLength: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
            minLength: config.min,
            currentLength: control.value.length,
          },
        };
      }
      return undefined;
    };
  }

  public maxLength(config: IMaxLengthConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'Exceeds max length') as IMaxLengthConfig;
    return control => {
      if (control.value && control.value.length && control.value.length > config.max) {
        return {
          maxLength: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
            maxLength: config.max,
            currentLength: control.value.length,
          },
        };
      }
      return undefined;
    };
  }

  public numbersOnly(config?: IValidatorConfig): CustomValidatorFn {
    const regex = /^[0-9]*$/;
    config = this.baseConfig(config, 'Only numbers are expected');
    return control => {
      if (!regex.test(control.value) && control.value !== '') {
        return {
          numbersOnly: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public freeText(config?: IValidatorConfig): CustomValidatorFn {
    const regex = /^[\x20-\x7e\s]*$/; // Validate any ascii character from range 32-126 and any type of white space
    config = this.baseConfig(config, 'Only alphabet characters, numbers and symbols are expected');
    return control => {
      if (!regex.test(control.value) && control.value !== '') {
        return {
          freeText: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public dateFormat(config?: IValidatorConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'Incorrect format date');
    return control => {
      if (control.value && !dateFns.isValid(control.value)) {
        return {
          dateFormat: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public phoneNumber(config?: IValidatorConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'Only valid phone numbers are expected');
    if (!config?.regex) config.regex = /^[0-9\s\(\)\+\-]*$/;
    return control => {
      if (!config.regex.test(control.value) && control.value !== '') {
        return {
          phoneNumber: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public maxValue(config?: IValidatorConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'This value is not valid') as IMaxValueConfig;
    return control => {
      if (control.value && config.maxValue && control.value > config.maxValue) {
        return {
          maxValue: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public minValue(config?: IValidatorConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'This value is not valid') as IMinValueConfig;
    return control => {
      if (control.value && config.minValue && control.value < config.minValue) {
        return {
          minValue: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public onlyNumbersOrLetters(config?: IValidatorConfig): CustomValidatorFn {
    const regex = /(^([a-zA-Z]*)$)|(^([0-9]*)$)/;
    config = this.baseConfig(config, 'Only numbers or letters are valid');
    return control => {
      if (!regex.test(control.value) && control.value !== '') {
        return {
          onlyNumbersOrLetters: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public onlyLettersAndSomeCharacters(config?: IValidatorConfig): CustomValidatorFn {
    const regex = /^[a-zA-Z',.\-\s]*$/;
    config = this.baseConfig(config, 'You entered an invalid character, please check your information');
    return control => {
      if (!regex.test(control.value) && control.value !== '') {
        return {
          onlyLettersAndSomeCharacters: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public dateRange(config?: IDateRangeConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'Invalid date range') as IDateRangeConfig;
    return control => {
      if (control.value && config.endDate.value && control.value > config.endDate.value) {
        return {
          dateRange: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public emailFormat(config?: IValidatorConfig): CustomValidatorFn {
    // tslint:disable-next-line:max-line-length
    const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    config = this.baseConfig(config, 'Invalid email address');
    return control => {
      if (control.value && !regex.test(control.value) && control.value !== '') {
        return {
          emailFormat: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public fileExtension(config: IFileExtensionConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'Invalid file extension') as IFileExtensionConfig;
    return control => {
      // control.value must be a File
      // config.extensions must be a collection
      if (control.value && !config.extensions.includes(control.value.type)) {
        return {
          fileExtension: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  public fileSize(config: IFileSizeConfig): CustomValidatorFn {
    config = this.baseConfig(config, 'Invalid file size') as IFileSizeConfig;
    return control => {
      // control.value must be a File
      // 1 MB = 1048576 Bytes
      if (control.value && control.value.size / 1048576 > config.maxSize) {
        return {
          fileSize: {
            showErrorMessageOn: config.showErrorMessageOn,
            priority: config.priority,
            message: config.message,
          },
        };
      }
      return undefined;
    };
  }

  private baseConfig(config: IValidatorConfig, message = ''): ICustomError {
    if (!config) config = {};
    if (!config.showErrorMessageOn) config.showErrorMessageOn = ValidationShowType.Touched;
    if (!config.priority) config.priority = 0;
    if (!config.message) config.message = message;
    return config as ICustomError;
  }
}
